import { AxiosResponse } from "axios";
import { call, put, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../../common/axios";
import { mapPhasesApiToUi } from "../../../slices/technology-settings/phases/phaseMapper";
import { technologySettingsPhasesSliceAction } from "../../../slices/technology-settings/phases";
import type {
  ActivityPayloadType,
  CreateActivityApiResponse,
  CreateActivitySagaPayloadType,
  CreatePhaseApiResponse,
  CreatePhasePayloadType,
  CreatePhaseSagaPayloadType,
  DeleteActivityApiResponse,
  DeleteActivitySagaPayloadType,
  DeletePhaseApiResponse,
  DeletePhaseSagaPayloadType,
  GetPhasesApiResponse,
  UpdateActivityApiResponse,
  UpdateActivitySagaPayloadType,
  ReorderPhasesApiResponse,
  ReorderPhasesPayloadType,
  ReorderPhasesSagaPayloadType,
  UpdatePhaseApiResponse,
  UpdatePhasePayloadType,
  UpdatePhaseSagaPayloadType,
} from "../../../slices/technology-settings/phases/Types";
import {
  getPhasesSagaAction,
  technologySettingsPhasesSagaActionTypes,
} from "./saga-actions";

const getPhasesAPI = async () => {
  const response = await axiosRestInstance.get<
    AxiosResponse<GetPhasesApiResponse>
  >("/technology-settings/phase/getData");
  return response.data;
};

const createPhaseAPI = async (payload: CreatePhasePayloadType) => {
  const response = await axiosRestInstance.post<
    AxiosResponse<CreatePhaseApiResponse>
  >("/technology-settings/phase/insert", payload);
  return response.data;
};

const updatePhaseAPI = async (
  id: string | number,
  payload: UpdatePhasePayloadType
) => {
  const response = await axiosRestInstance.put<
    AxiosResponse<UpdatePhaseApiResponse>
  >(`/technology-settings/phase/update/${id}`, payload);
  return response.data;
};

const createActivityAPI = async (
  phaseId: string | number,
  payload: ActivityPayloadType
) => {
  const response = await axiosRestInstance.post<
    AxiosResponse<CreateActivityApiResponse>
  >(`/technology-settings/${phaseId}/activity/insert`, payload);
  return response.data;
};

const updateActivityAPI = async (
  id: string | number,
  payload: ActivityPayloadType
) => {
  const response = await axiosRestInstance.put<
    AxiosResponse<UpdateActivityApiResponse>
  >(`/technology-settings/activity/update/${id}`, payload);
  return response.data;
};

const reorderPhasesAPI = async (payload: ReorderPhasesPayloadType) => {
  const response = await axiosRestInstance.put<
    AxiosResponse<ReorderPhasesApiResponse>
  >("/technology-settings/reorder/phases", payload);
  return response.data;
};

const deletePhaseAPI = async (id: string | number) => {
  const response = await axiosRestInstance.delete<
    AxiosResponse<DeletePhaseApiResponse>
  >(`/technology-settings/phase/delete/${id}`);
  return response.data;
};

const deleteActivityAPI = async (id: string | number) => {
  const response = await axiosRestInstance.delete<
    AxiosResponse<DeleteActivityApiResponse>
  >(`/technology-settings/activity/delete/${id}`);
  return response.data;
};

function* getPhasesSaga() {
  try {
    yield put(technologySettingsPhasesSliceAction.setPhasesLoading(true));
    const response: GetPhasesApiResponse = yield call(getPhasesAPI);
    if (response.success) {
      yield put(
        technologySettingsPhasesSliceAction.setPhases(
          mapPhasesApiToUi(response.data || [])
        )
      );
    } else {
      yield put(technologySettingsPhasesSliceAction.setPhases([]));
    }
  } catch (e) {
    yield put(technologySettingsPhasesSliceAction.setPhases([]));
    console.log(e);
  } finally {
    yield put(technologySettingsPhasesSliceAction.setPhasesLoading(false));
  }
}

function* createPhaseSaga(action: {
  type: string;
  payload: CreatePhaseSagaPayloadType;
}) {
  try {
    yield put(technologySettingsPhasesSliceAction.setCreatePhaseLoading(true));
    const response: CreatePhaseApiResponse = yield call(
      createPhaseAPI,
      action.payload.data
    );
    if (response.success) {
      yield put(getPhasesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Phase created successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(response.message || "Unable to create phase.");
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to create phase."
      );
    }
    console.log(e);
  } finally {
    yield put(technologySettingsPhasesSliceAction.setCreatePhaseLoading(false));
  }
}

function* updatePhaseSaga(action: {
  type: string;
  payload: UpdatePhaseSagaPayloadType;
}) {
  try {
    yield put(technologySettingsPhasesSliceAction.setUpdatePhaseLoading(true));
    const response: UpdatePhaseApiResponse = yield call(
      updatePhaseAPI,
      action.payload.id,
      action.payload.data
    );
    if (response.success) {
      yield put(getPhasesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Phase updated successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(response.message || "Unable to update phase.");
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to update phase."
      );
    }
    console.log(e);
  } finally {
    yield put(technologySettingsPhasesSliceAction.setUpdatePhaseLoading(false));
  }
}

function* createActivitySaga(action: {
  type: string;
  payload: CreateActivitySagaPayloadType;
}) {
  try {
    yield put(technologySettingsPhasesSliceAction.setCreateActivityLoading(true));
    const response: CreateActivityApiResponse = yield call(
      createActivityAPI,
      action.payload.phaseId,
      action.payload.data
    );
    if (response.success) {
      yield put(getPhasesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Activity created successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(response.message || "Unable to create activity.");
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to create activity."
      );
    }
    console.log(e);
  } finally {
    yield put(technologySettingsPhasesSliceAction.setCreateActivityLoading(false));
  }
}

function* updateActivitySaga(action: {
  type: string;
  payload: UpdateActivitySagaPayloadType;
}) {
  try {
    yield put(technologySettingsPhasesSliceAction.setUpdateActivityLoading(true));
    const response: UpdateActivityApiResponse = yield call(
      updateActivityAPI,
      action.payload.id,
      action.payload.data
    );
    if (response.success) {
      yield put(getPhasesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Activity updated successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(response.message || "Unable to update activity.");
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to update activity."
      );
    }
    console.log(e);
  } finally {
    yield put(technologySettingsPhasesSliceAction.setUpdateActivityLoading(false));
  }
}

function* reorderPhasesSaga(action: {
  type: string;
  payload: ReorderPhasesSagaPayloadType;
}) {
  try {
    yield put(technologySettingsPhasesSliceAction.setReorderPhasesLoading(true));
    const response: ReorderPhasesApiResponse = yield call(
      reorderPhasesAPI,
      action.payload.data
    );
    if (response.success) {
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Phase order updated successfully."
        );
      }
    } else {
      yield put(getPhasesSagaAction());
      if (action.payload.onError) {
        action.payload.onError(response.message || "Unable to reorder phases.");
      }
    }
  } catch (e: any) {
    yield put(getPhasesSagaAction());
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to reorder phases."
      );
    }
    console.log(e);
  } finally {
    yield put(technologySettingsPhasesSliceAction.setReorderPhasesLoading(false));
  }
}

function* deletePhaseSaga(action: {
  type: string;
  payload: DeletePhaseSagaPayloadType;
}) {
  try {
    yield put(technologySettingsPhasesSliceAction.setDeletePhaseLoading(true));
    const response: DeletePhaseApiResponse = yield call(
      deletePhaseAPI,
      action.payload.id
    );
    if (response.success) {
      yield put(getPhasesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Phase deleted successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(response.message || "Unable to delete phase.");
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to delete phase."
      );
    }
    console.log(e);
  } finally {
    yield put(technologySettingsPhasesSliceAction.setDeletePhaseLoading(false));
  }
}

function* deleteActivitySaga(action: {
  type: string;
  payload: DeleteActivitySagaPayloadType;
}) {
  try {
    yield put(technologySettingsPhasesSliceAction.setDeleteActivityLoading(true));
    const response: DeleteActivityApiResponse = yield call(
      deleteActivityAPI,
      action.payload.id
    );
    if (response.success) {
      yield put(getPhasesSagaAction());
      if (action.payload.onSuccess) {
        action.payload.onSuccess(
          response.message || "Activity deleted successfully."
        );
      }
    } else if (action.payload.onError) {
      action.payload.onError(response.message || "Unable to delete activity.");
    }
  } catch (e: any) {
    if (action.payload.onError) {
      action.payload.onError(
        e?.response?.data?.message || "Unable to delete activity."
      );
    }
    console.log(e);
  } finally {
    yield put(technologySettingsPhasesSliceAction.setDeleteActivityLoading(false));
  }
}

function* technologySettingsPhasesSaga() {
  yield takeLatest(
    technologySettingsPhasesSagaActionTypes.callGetPhasesSaga,
    getPhasesSaga
  );
  yield takeLatest(
    technologySettingsPhasesSagaActionTypes.callCreatePhaseSaga,
    createPhaseSaga
  );
  yield takeLatest(
    technologySettingsPhasesSagaActionTypes.callUpdatePhaseSaga,
    updatePhaseSaga
  );
  yield takeLatest(
    technologySettingsPhasesSagaActionTypes.callCreateActivitySaga,
    createActivitySaga
  );
  yield takeLatest(
    technologySettingsPhasesSagaActionTypes.callUpdateActivitySaga,
    updateActivitySaga
  );
  yield takeLatest(
    technologySettingsPhasesSagaActionTypes.callReorderPhasesSaga,
    reorderPhasesSaga
  );
  yield takeLatest(
    technologySettingsPhasesSagaActionTypes.callDeletePhaseSaga,
    deletePhaseSaga
  );
  yield takeLatest(
    technologySettingsPhasesSagaActionTypes.callDeleteActivitySaga,
    deleteActivitySaga
  );
}

export default technologySettingsPhasesSaga;
